import React, { useEffect } from 'react';
import { cn } from '@rocco/utils/cn';
import { CloseLine } from '@archipro-design/icons';
import { Spinner } from '@rocco/components/spinner';

import './tagging-modal-shell.css';

interface TaggingModalShellProps {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    /** Accessible name for the dialog (used when there is no visible heading). */
    'aria-label'?: string;
    /** Called when the user presses Escape while the modal is mounted. */
    onDismiss?: () => void;
}

/**
 * Unified layout shell for all product-tagging modal screens.
 * Provides consistent width, border-radius, shadow and font-family.
 * Compose with TaggingModalHeader, TaggingModalContent and
 * TaggingModalFooterButton for a complete modal.
 *
 * Co-located `tagging-modal-shell.css` forces caret + value text colour on fields (see file comment).
 */
const TaggingModalShell = ({
    children,
    className,
    style,
    'aria-label': ariaLabel,
    onDismiss,
}: TaggingModalShellProps) => {
    useEffect(() => {
        if (!onDismiss) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onDismiss();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onDismiss]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className={cn(
                'tagging-modal-shell relative flex w-90 flex-col overflow-hidden rounded-lg font-sans',
                'bg-gray-50/70 backdrop-blur-[5px]',
                'shadow-[inset_0px_0px_4px_0px_rgba(0,0,0,0.25)]',
                className,
            )}
            style={style}
        >
            {children}
        </div>
    );
};

interface TaggingModalHeaderProps {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

/** Non-scrolling header area — search input or static title. */
const TaggingModalHeader = React.forwardRef<
    HTMLDivElement,
    TaggingModalHeaderProps
>(({ children, className, style }, ref) => (
    <div ref={ref} className={cn('shrink-0 p-4', className)} style={style}>
        {children}
    </div>
));
TaggingModalHeader.displayName = 'TaggingModalHeader';

/**
 * Solid 2px rule under a field row — same visual as `TaggingModalStaticHeader`’s separator.
 * Used instead of Aria TextInput’s Fluent divider pseudo-element, which thins under compositing
 * (Popper transform + backdrop blur). Height/margin live in `tagging-modal-shell.css`.
 */
const TaggingModalFieldUnderline = () => (
    <div className="tagging-modal-field-underline" aria-hidden />
);

interface TaggingModalStaticHeaderProps {
    title: string;
    message?: React.ReactNode;
    onClose: () => void;
}

/**
 * Mirrors the responsive Aria TextInput `size="md"` row metrics so static headers stay vertically
 * aligned with the search-input header across viewports.
 */
const TaggingModalStaticHeader = ({
    title,
    message,
    onClose,
}: TaggingModalStaticHeaderProps) => (
    <div className="shrink-0 p-4">
        <div className="tagging-modal-static-header-row">
            <h3 className="tagging-modal-static-header-title">{title}</h3>
            <button
                type="button"
                onClick={onClose}
                className="tagging-modal-static-header-close"
                aria-label="Close"
            >
                <CloseLine className="tagging-modal-static-header-close-icon" />
            </button>
        </div>
        <TaggingModalFieldUnderline />

        {message && (
            <p className="text-neutral-600 pt-3 text-sm leading-relaxed">
                {message}
            </p>
        )}
    </div>
);

interface TaggingModalContentProps {
    children: React.ReactNode;
    className?: string;
}

/** Scrollable content area for search results or form fields. */
const TaggingModalContent = ({
    children,
    className,
}: TaggingModalContentProps) => (
    <div
        className={cn(
            'scrollbar-hidden min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain',
            className,
        )}
    >
        {children}
    </div>
);

interface TaggingModalFooterButtonProps {
    label: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    loading?: boolean;
    type?: 'button' | 'submit';
}

/** Full-width dark footer button, used across all tagging modal screens. */
const TaggingModalFooterButton = ({
    label,
    onClick,
    disabled = false,
    loading = false,
    type = 'button',
}: TaggingModalFooterButtonProps) => (
    <div className="shrink-0 border-t border-gray-200 p-4">
        <button
            type={type}
            onClick={onClick}
            disabled={disabled || loading}
            className={cn(
                'flex w-full items-center justify-center border border-black bg-black py-3',
                'font-sans text-sm font-medium uppercase tracking-wider text-white transition-opacity',
                'hover:opacity-90 disabled:opacity-50',
            )}
        >
            {loading ? <Spinner className="size-5 text-white" /> : label}
        </button>
    </div>
);

/** Full-width outlined (ghost) footer button — used for secondary actions like "Remove Tag". */
const TaggingModalFooterButtonOutline = ({
    label,
    onClick,
    disabled = false,
    type = 'button',
}: TaggingModalFooterButtonProps) => (
    <div className="shrink-0 px-4 pb-4 pt-0">
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={cn(
                'w-full border border-black py-3 font-sans text-sm font-medium uppercase',
                'tracking-wider text-black transition-opacity',
                'hover:bg-black/5 disabled:opacity-50',
            )}
        >
            {label}
        </button>
    </div>
);

interface TaggingModalLoadingOverlayProps {
    children: React.ReactNode;
}

/** Absolute overlay while a search is in flight — same tint as result row hover. */
const TaggingModalLoadingOverlay = ({
    children,
}: TaggingModalLoadingOverlayProps) => (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[rgba(0,0,0,0.06)]">
        {children}
    </div>
);

export {
    TaggingModalShell,
    TaggingModalHeader,
    TaggingModalFieldUnderline,
    TaggingModalStaticHeader,
    TaggingModalContent,
    TaggingModalFooterButton,
    TaggingModalFooterButtonOutline,
    TaggingModalLoadingOverlay,
};
